import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import jsQR from 'jsqr';
import { db } from '../../../firebase/config';
import { callClaude } from '../../../utils/claudeProxy';
import { doc, getDoc, addDoc, collection, serverTimestamp, updateDoc, increment } from 'firebase/firestore';
import {
  Box, Typography, Button, CircularProgress, Paper, IconButton,
  TextField, Alert, Chip,
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';

const accent = '#E05500';
const bg     = '#000';
const surface = '#1c1c1e';

const QUAD_INFO = {
  strength:    { letter: 'S', label: 'Styrkor',     color: '#4CAF50' },
  weakness:    { letter: 'W', label: 'Svagheter',   color: '#EF5350' },
  opportunity: { letter: 'O', label: 'Möjligheter', color: '#42A5F5' },
  threat:      { letter: 'T', label: 'Hot',          color: '#E05500' },
};

// ── QR-avkodning ───────────────────────────────────────────────────────────────
async function decodeQRFromFile(file) {
  if ('BarcodeDetector' in window) {
    try {
      const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
      const bitmap = await createImageBitmap(file);
      const codes = await detector.detect(bitmap);
      if (codes.length > 0) return codes[0].rawValue;
    } catch { /* fall through */ }
  }
  try {
    const bitmap = await createImageBitmap(file);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width; canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bitmap, 0, 0);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const code = jsQR(imageData.data, imageData.width, imageData.height, { inversionAttempts: 'dontInvert' });
    if (code) return code.data;
  } catch { /* fall through */ }
  return null;
}

function parseSwotQRUrl(raw) {
  try {
    const url = new URL(raw);
    const q = url.searchParams.get('q');
    const g = url.searchParams.get('g') || null;
    const s = url.searchParams.get('s');
    if (q && QUAD_INFO[q] && s) return { quadrant: q, groupId: g, sessionId: s };
  } catch { /* not a URL */ }
  return null;
}

// ── Bidragslista ───────────────────────────────────────────────────────────────
function ContributionList({ items, onEdit, onDelete }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {items.map((item, i) => (
        <Box key={i} sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          bgcolor: surface, borderRadius: '10px', px: 1.5, py: 0.75,
          border: '1px solid #2a2a2a',
        }}>
          <Typography sx={{ flex: 1, fontSize: '0.88rem', color: '#e0e0e0', lineHeight: 1.4 }}>
            {item}
          </Typography>
          <IconButton size="small" onClick={() => onEdit(i)}
            sx={{ color: '#555', '&:hover': { color: '#fff' }, p: 0.5 }}>
            <EditIcon sx={{ fontSize: 14 }} />
          </IconButton>
          <IconButton size="small" onClick={() => onDelete(i)}
            sx={{ color: '#555', '&:hover': { color: '#ef4444' }, p: 0.5 }}>
            <DeleteIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      ))}
    </Box>
  );
}

// ── Huvud-komponent ────────────────────────────────────────────────────────────
export default function SwotNScan() {
  const [searchParams] = useSearchParams();
  const urlSessionId = searchParams.get('s');
  const urlQuadrant  = searchParams.get('q');
  const urlGroupId   = searchParams.get('g');

  const [session,   setSession]   = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');

  const [quadrant,  setQuadrant]  = useState(QUAD_INFO[urlQuadrant] ? urlQuadrant : null);
  const [groupId,   setGroupId]   = useState(urlGroupId || null);

  const [imageFile,    setImageFile]    = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [qrStatus,     setQrStatus]     = useState(null);
  const [analyzing,    setAnalyzing]    = useState(false);

  const [contributions, setContributions] = useState(null); // null = inte analyserat ännu
  const [editIdx,  setEditIdx]  = useState(null);
  const [editText, setEditText] = useState('');
  const [newText,  setNewText]  = useState('');

  const [warning, setWarning] = useState('');
  const [saving,  setSaving]  = useState(false);
  const [done,    setDone]    = useState(false);
  const [savedCount, setSavedCount] = useState(0);

  const fileRef = useRef();

  // Ladda session
  useEffect(() => {
    if (!urlSessionId) { setError('Ogiltig länk — session saknas.'); setLoading(false); return; }
    getDoc(doc(db, 'swot_sessions', urlSessionId)).then(snap => {
      if (!snap.exists()) { setError('SWOT-sessionen hittades inte.'); }
      else { setSession({ id: snap.id, ...snap.data() }); }
      setLoading(false);
    });
  }, [urlSessionId]);

  const quadInfo = quadrant ? QUAD_INFO[quadrant] : null;
  const group    = session?.groups?.find(g => g.id === groupId) || null;

  // ── Filhantering + QR ───────────────────────────────────────────────────────
  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setContributions(null);
    setWarning('');

    if (quadrant) return; // kvadrant redan känd från URL
    setQrStatus('detecting');
    const raw = await decodeQRFromFile(file);
    if (raw) {
      const parsed = parseSwotQRUrl(raw);
      if (parsed && parsed.sessionId === urlSessionId) {
        setQuadrant(parsed.quadrant);
        setGroupId(parsed.groupId);
        setQrStatus('found');
        return;
      }
    }
    setQrStatus('not_found');
  }

  // ── AI-analys med Claude Vision ──────────────────────────────────────────────
  async function analyze() {
    if (!imageFile || !quadrant) return;
    setAnalyzing(true);
    setContributions(null);
    setWarning('');

    try {
      const base64 = await new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = () => res(reader.result.split(',')[1]);
        reader.onerror = rej;
        reader.readAsDataURL(imageFile);
      });

      const qInfo = QUAD_INFO[quadrant];
      const groupContext = group ? `Grupp: "${group.name}"` : '';

      const prompt = `Du analyserar ett handskrivet hybridblad från en SWOT-analys.

Kvadrant: ${qInfo.label} (${qInfo.letter})
Session: "${session?.title || ''}"
${groupContext}
${session?.question ? `Frågeställning: "${session.question}"` : ''}

UPPGIFT:
1. Extrahera ALLA handskrivna bidrag från skrivytan på bladet.
   Varje rad, punkt, bindestreck eller tydligt avgränsat bidrag = ett separat bidrag.
   Rensa upp stavning om handstilen är otydlig men ändra inte innebörden.

2. Om kvadrantbeteckningen (${qInfo.letter} / ${qInfo.label}) på bladet INTE stämmer med ovan — notera det som "mismatch: true".

3. Extrahera deltagarnamn från DELTAGARE-sektionen i sidfoten (om de finns).

Returnera ENBART giltig JSON (inga kodblock):
{
  "contributions": ["bidrag 1", "bidrag 2", "..."],
  "participantNames": ["Förnamn Efternamn", "..."],
  "mismatch": false
}`;

      const data = await callClaude({
        model: 'claude-sonnet-4-6',
        max_tokens: 1000,
        messages: [{
          role: 'user',
          content: [
            { type: 'image', source: { type: 'base64', media_type: imageFile.type || 'image/jpeg', data: base64 } },
            { type: 'text', text: prompt },
          ],
        }],
      });
      const raw = (data.content?.[0]?.text || '{}').replace(/```json|```/g, '').trim();
      let parsed;
      try { parsed = JSON.parse(raw); } catch { parsed = {}; }

      const extracted = Array.isArray(parsed.contributions) ? parsed.contributions.filter(Boolean) : [];
      setContributions(extracted);

      if (parsed.mismatch) {
        setWarning(`OBS: AI hittade en annan kvadrant på bladet än förväntat (${qInfo.label}). Kontrollera att du fotograferade rätt blad.`);
      }

    } catch {
      setWarning('Analysen misslyckades — försök igen.');
    } finally {
      setAnalyzing(false);
    }
  }

  // ── Redigera bidrag ─────────────────────────────────────────────────────────
  const startEdit = (i) => { setEditIdx(i); setEditText(contributions[i]); };
  const saveEdit  = () => {
    if (editText.trim()) {
      setContributions(prev => prev.map((c, i) => i === editIdx ? editText.trim() : c));
    }
    setEditIdx(null); setEditText('');
  };
  const deleteItem  = (i) => setContributions(prev => prev.filter((_, idx) => idx !== i));
  const addItem     = () => {
    if (newText.trim()) { setContributions(prev => [...prev, newText.trim()]); setNewText(''); }
  };

  // ── Spara till Firestore ─────────────────────────────────────────────────────
  async function handleSave() {
    if (!contributions?.length || !quadrant) return;
    setSaving(true);
    try {
      for (const text of contributions) {
        await addDoc(collection(db, 'swot_sessions', urlSessionId, 'cards'), {
          quadrant,
          text,
          author: group?.name || 'Hybridskan',
          source: 'ocr',
          groupId: groupId || null,
          createdAt: serverTimestamp(),
        });
      }
      await updateDoc(doc(db, 'swot_sessions', urlSessionId), {
        cardCount: increment(contributions.length),
      });
      setSavedCount(contributions.length);
      setDone(true);
    } finally {
      setSaving(false);
    }
  }

  // ── Laddning / fel ──────────────────────────────────────────────────────────
  if (loading) return (
    <Box sx={{ minHeight: '100vh', bgcolor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress sx={{ color: accent }} />
    </Box>
  );

  if (error) return (
    <Box sx={{ minHeight: '100vh', bgcolor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
      <Paper sx={{ p: 3, borderRadius: 2, bgcolor: surface, maxWidth: 360 }}>
        <Typography color="error" fontWeight={600}>{error}</Typography>
      </Paper>
    </Box>
  );

  // ── Klart-vy ────────────────────────────────────────────────────────────────
  if (done) return (
    <Box sx={{
      minHeight: '100vh', bgcolor: bg, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', p: 3, gap: 3,
    }}>
      <CheckCircleOutlineIcon sx={{ fontSize: 72, color: quadInfo?.color || '#4CAF50' }} />
      <Typography fontWeight={800} fontSize="1.5rem" color="#fff" textAlign="center">
        {savedCount} bidrag sparade!
      </Typography>
      <Typography color="#888" textAlign="center" fontSize="0.9rem">
        {quadInfo?.label} — {session?.title}
      </Typography>
      <Button
        onClick={() => { setDone(false); setImageFile(null); setImagePreview(null); setContributions(null); setWarning(''); }}
        variant="outlined"
        sx={{ color: quadInfo?.color || accent, borderColor: quadInfo?.color || accent, textTransform: 'none', fontWeight: 600 }}
      >
        Skanna ett blad till
      </Button>
    </Box>
  );

  // ── Huvud-vy ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: bg, color: '#e0e0e0', fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}>

      {/* Header */}
      <Box sx={{
        px: 2, pt: 3, pb: 2,
        borderBottom: quadInfo ? `3px solid ${quadInfo.color}` : `3px solid ${accent}`,
      }}>
        <Typography fontSize="0.62rem" color="#555" fontWeight={700} letterSpacing={1} mb={0.25}>
          SWOT-ANALYS · HYBRIDSKANNING
        </Typography>
        <Typography fontWeight={800} fontSize="1.1rem" color="#fff">{session?.title}</Typography>
        {session?.question && (
          <Typography fontSize="0.78rem" color="#666" mt={0.25} fontStyle="italic">
            {session.question}
          </Typography>
        )}

        {/* Kvadrant-chip */}
        {quadInfo && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
            <Box sx={{
              width: 32, height: 32, borderRadius: '8px',
              bgcolor: quadInfo.color + '25',
              border: `2px solid ${quadInfo.color}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Typography fontWeight={900} fontSize="1rem" color={quadInfo.color}>{quadInfo.letter}</Typography>
            </Box>
            <Typography fontWeight={700} fontSize="1rem" color={quadInfo.color}>{quadInfo.label}</Typography>
            {group && (
              <Chip label={group.name} size="small"
                sx={{ bgcolor: (group.color || '#444') + '33', color: group.color || '#aaa',
                  border: `1px solid ${group.color || '#444'}`, fontSize: '0.72rem', height: 22 }} />
            )}
          </Box>
        )}
      </Box>

      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2.5, maxWidth: 540, mx: 'auto' }}>

        {/* QR-status */}
        {qrStatus === 'not_found' && !quadrant && (
          <Alert severity="warning" sx={{ borderRadius: 2, fontSize: '0.82rem' }}>
            QR-kod hittades inte i bilden. Kvadranten identifieras automatiskt av AI vid analys, eller välj manuellt nedan.
          </Alert>
        )}

        {/* Foto-uppladdning */}
        <Box>
          <input type="file" ref={fileRef} hidden accept="image/*" capture="environment"
            onChange={handleFileChange} />

          {!imagePreview ? (
            <Box
              onClick={() => fileRef.current?.click()}
              sx={{
                border: `2px dashed ${quadInfo?.color || accent}55`,
                borderRadius: '16px',
                p: 4, textAlign: 'center',
                cursor: 'pointer',
                bgcolor: surface,
                transition: 'border-color 0.2s',
                '&:hover': { borderColor: quadInfo?.color || accent },
              }}
            >
              <CameraAltIcon sx={{ fontSize: 48, color: quadInfo?.color || accent, mb: 1 }} />
              <Typography fontWeight={600} color="#fff">Fotografera bladet</Typography>
              <Typography fontSize="0.78rem" color="#666" mt={0.5}>
                Hela bladet ska synas, inkl. QR-koden i sidfoten
              </Typography>
            </Box>
          ) : (
            <Box>
              <Box component="img" src={imagePreview} alt="Skanning"
                sx={{ width: '100%', borderRadius: '12px', display: 'block', maxHeight: 280, objectFit: 'cover' }} />
              <Button
                size="small"
                startIcon={<AddPhotoAlternateIcon />}
                onClick={() => fileRef.current?.click()}
                sx={{ mt: 1, color: '#555', textTransform: 'none', fontSize: '0.78rem' }}
              >
                Byt bild
              </Button>
            </Box>
          )}
        </Box>

        {/* Analysera-knapp */}
        {imageFile && !analyzing && contributions === null && (
          <Button
            variant="contained"
            onClick={analyze}
            disabled={!quadrant}
            startIcon={<AutoAwesomeIcon />}
            fullWidth
            sx={{ bgcolor: quadInfo?.color || accent, '&:hover': { filter: 'brightness(0.85)' }, textTransform: 'none', fontWeight: 700, borderRadius: 2, py: 1.25 }}
          >
            Analysera med AI
          </Button>
        )}

        {analyzing && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: surface, borderRadius: 2, p: 2 }}>
            <CircularProgress size={20} sx={{ color: quadInfo?.color || accent }} />
            <Typography fontSize="0.88rem" color="#aaa">Extraherar bidrag…</Typography>
          </Box>
        )}

        {/* Varning */}
        {warning && (
          <Alert severity="warning" sx={{ borderRadius: 2, fontSize: '0.82rem' }}>{warning}</Alert>
        )}

        {/* Bidragslista */}
        {contributions !== null && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography fontWeight={700} fontSize="0.88rem" color="#fff">
                {contributions.length} bidrag extraherade
              </Typography>
              <Button size="small" onClick={analyze}
                sx={{ color: '#555', textTransform: 'none', fontSize: '0.72rem' }}>
                Kör om
              </Button>
            </Box>

            {contributions.length === 0 && (
              <Typography fontSize="0.82rem" color="#555" fontStyle="italic">
                Inga bidrag hittades — ta ett tydligare foto eller lägg till manuellt.
              </Typography>
            )}

            {/* Redigerbart bidrag */}
            {editIdx !== null ? (
              <Box sx={{ bgcolor: surface, borderRadius: 2, p: 1.5, mb: 0.75 }}>
                <TextField
                  autoFocus fullWidth multiline
                  value={editText} onChange={e => setEditText(e.target.value)}
                  size="small"
                  sx={{
                    mb: 1,
                    '& .MuiOutlinedInput-root': { color: '#fff', bgcolor: '#0a0a0a', fontSize: '0.88rem' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: quadInfo?.color || accent },
                  }}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" variant="contained" onClick={saveEdit}
                    sx={{ bgcolor: quadInfo?.color || accent, textTransform: 'none', fontWeight: 600, fontSize: '0.78rem' }}>
                    Spara
                  </Button>
                  <Button size="small" onClick={() => setEditIdx(null)}
                    sx={{ color: '#555', textTransform: 'none', fontSize: '0.78rem' }}>
                    Avbryt
                  </Button>
                </Box>
              </Box>
            ) : (
              <ContributionList items={contributions} onEdit={startEdit} onDelete={deleteItem} />
            )}

            {/* Lägg till manuellt */}
            <Box sx={{ display: 'flex', gap: 1, mt: 1.25 }}>
              <TextField
                size="small" placeholder="Lägg till bidrag manuellt…"
                value={newText} onChange={e => setNewText(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addItem()}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': { color: '#fff', bgcolor: surface, fontSize: '0.82rem', borderRadius: '10px' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333' },
                }}
              />
              <IconButton onClick={addItem} disabled={!newText.trim()}
                sx={{ bgcolor: quadInfo?.color || accent, color: '#fff', borderRadius: '10px', px: 1.5,
                  '&:hover': { filter: 'brightness(0.85)' }, '&.Mui-disabled': { opacity: 0.3 } }}>
                <AddIcon />
              </IconButton>
            </Box>
          </Box>
        )}

        {/* Spara */}
        {contributions !== null && contributions.length > 0 && (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            fullWidth
            startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <CheckCircleOutlineIcon />}
            sx={{
              bgcolor: quadInfo?.color || accent,
              '&:hover': { filter: 'brightness(0.85)' },
              textTransform: 'none', fontWeight: 700,
              borderRadius: 2, py: 1.25, fontSize: '0.95rem',
            }}
          >
            {saving ? 'Sparar…' : `Spara ${contributions.length} bidrag till boardet`}
          </Button>
        )}
      </Box>
    </Box>
  );
}
